/**
 * These localised error messages are returned by the @op/validate string
 * validators.
 *
 * By default, the messages are expected to be used without any prefix.
 *
 * In case there would be an application-provided prefix, then the value
 * of '-part' is used.
 *
 * Note that some validators use other validators internally, and may in turn
 * produce their errors. In particular:
 *
 * The digits validator is called from:
 *   card, phone-number, pohjola-party-id, postal-code-finland, reference-number
 *
 * The string validator is called from:
 *   bic, business-id-finland, creditor-reference, digits, email, iban,
 *   op-identifier,pohjola-agreement-id, pohjola-claim-id, ssn-estonia-lithuania,
 *   ssn-finland, ssn-latvia
 *
 * Additionally:
 *   op-iban calls the iban validator, and
 *   plussa-card calls the card validator.
 */
export default {
    // From: digits, business-id-finland
    // String consists of only '0' characters
    'all-zeros': {
        en: 'Check the information. The information cannot contain only zeros.',
        'en-part': 'cannot contain only zeros',
        fi: 'Tarkista tieto. Tieto ei voi sisältää vain nollia.',
        'fi-part': 'ei voi sisältää vain nollia',
        sv: 'Kontrollera uppgiften. Uppgiften kan inte innehålla endast nollor.',
        'sv-part': 'kan inte innehålla endast nollor'
    },
    // From: bic, card, plussa-card
    // incorrect length
    'bad-length': {
        en: 'Check the length.',
        'en-part': 'has incorrect length',
        fi: 'Tarkista pituus.',
        'fi-part': 'on väärän pituinen',
        sv: 'Kontrollera längden.',
        'sv-part': 'har fel längd'
    },
    // From: creditor-reference
    // Does not start with letters RF
    'creditor-ref-start': {
        en: 'International reference numbers must begin with the letters "RF".',
        'en-part': 'does not begin with "RF"',
        fi: 'Aloita kansainvälinen viitenumero kirjaimilla "RF".',
        'fi-part': 'ei ala "RF"',
        sv: 'Inled ett internationellt referensnummer med bokstäverna "RF".',
        'sv-part': 'börjar inte "RF"'
    },
    // From: email
    // email does not contain @ sign or the sign is in the beginning or the end of the email
    'email-missing-at-sign': {
        en: 'Check the email address. The @ symbol is missing or in the wrong place.',
        'en-part': 'does not contain the @ symbol in the correct place',
        fi: 'Tarkista sähköpostiosoite. @-merkki puuttuu tai on virheellisessä kohdassa.',
        'fi-part': 'ei sisällä @-merkkiä oikeassa kohdassa',
        sv: 'Kontrollera e-postadressen. @-tecknet saknas eller är felplacerat.',
        'sv-part': 'innehåller inte @-tecken på rätt plats'
    },
    // From: string
    // mandatory information is missing
    // String is empty
    empty: {
        en: 'Fill in the missing mandatory information.',
        'en-part': 'is a mandatory information',
        fi: 'Täytä puuttuva pakollinen tieto.',
        'fi-part': 'on pakollinen tieto',
        sv: 'Fyll i obligatorisk uppgift som saknas.',
        'sv-part': 'är obligatorisk information'
    },
    // From: ssn-estonia-lithuania, ssn-finland, ssn-latvia
    // Personal identity code day part is not in the range 1..31
    'impossible-day': {
        en: 'Check the first part of the personal identity code.',
        'en-part': 'contains an error in the first part of the personal identity code',
        fi: 'Tarkista henkilötunnuksen alkuosa.',
        'fi-part': 'sisältää virheen henkilötunnuksen alkuosassa',
        sv: 'Kontrollera personbeteckningens början.',
        'sv-part': 'innehåller ett fel i början av personbeteckningen'
    },
    // From: ssn-estonia-lithuania, ssn-finland, ssn-latvia
    // Personal identity code month part is not in the range 1..12
    'impossible-month': {
        en: 'Check the first part of the personal identity code.',
        'en-part': 'contains an error in the first part of the personal identity code',
        fi: 'Tarkista henkilötunnuksen alkuosa.',
        'fi-part': 'sisältää virheen henkilötunnuksen alkuosassa',
        sv: 'Kontrollera personbeteckningens början.',
        'sv-part': 'innehåller ett fel i början av personbeteckningen'
    },
    // From: business-id-finland, iban
    // business id can contain only numbers, iban: only numbers and letters
    'invalid-chars': {
        en: 'Check the spelling.',
        'en-part': 'is incorrect',
        fi: 'Tarkista kirjoitusasu.',
        'fi-part': 'on virheellinen',
        sv: 'Kontrollera stavningen.',
        'sv-part': 'är inkorrekt'
    },
    // From: business-id-finland, card, op-identifier, pohjola-*-id, reference-number, ssn-*
    // check digit (usually the last character) is incorrect or can be correctly formed from an incorrect information
    'invalid-check-digit': {
        en: 'Check the spelling.',
        'en-part': 'is incorrect',
        fi: 'Tarkista kirjoitusasu.',
        'fi-part': 'on virheellinen',
        sv: 'Kontrollera stavningen.',
        'sv-part': 'är inkorrekt'
    },
    // From: phone-number
    // Country code part starts with other than '+' character
    'invalid-country-code': {
        en: 'Check the country code.',
        'en-part': 'contains an incorrect country code',
        fi: 'Tarkista maakoodi.',
        'fi-part': 'sisältää virheellisen maakoodin',
        sv: 'Kontrollera landskoden.',
        'sv-part': 'innehåller en inkorrekt landskod'
    },
    // From: ssn-estonia-lithuania, ssn-finland, ssn-latvia
    // Date part of personal identity code does not consist of six digits
    'invalid-date': {
        en: 'Check the first part of the personal identity code.',
        'en-part': 'contains an error in the first part of the personal identity code',
        fi: 'Tarkista henkilötunnuksen alkuosa.',
        'fi-part': 'sisältää virheen henkilötunnuksen alkuosassa',
        sv: 'Kontrollera personbeteckningens början.',
        'sv-part': 'innehåller ett fel i början av personbeteckningen'
    },
    // From: ssn-estonia-lithuania, ssn-finland, ssn-latvia
    // Non-date number part of personal identity code is not numeric
    // Three first characters after the punctuation mark are not numeric
    'invalid-number': {
        en: 'Check the second part of the personal identity code.',
        'en-part': 'contains an error in the second part of the personal identity code',
        fi: 'Tarkista henkilötunnuksen loppuosa.',
        'fi-part': 'sisältää virheen henkilötunnuksen loppuosassa',
        sv: 'Kontrollera personbeteckningens slutdel.',
        'sv-part': 'innehåller ett fel i personbeteckningens slutdel'
    },
    // From: business-id-finland, ssn-finland
    // the punctuation mark is incorrect
    'invalid-separator': {
        en: 'Check the punctuation mark.',
        'en-part': 'contains an incorrect punctuation mark',
        fi: 'Tarkista välimerkki.',
        'fi-part': 'sisältää virheellisen välimerkin',
        sv: 'Kontrollera skiljetecken.',
        'sv-part': 'innehåller ett inkorrekt skiljetecken'
    },
    // From: ssn-estonia-lithuania
    // the first character (sex/century check digit) is incorrect
    'invalid-sex-digit': {
        en: 'Check the first character of the personal identity code.',
        'en-part': 'contains an error in the first character of the personal identity code',
        fi: 'Tarkista henkilötunnuksen ensimmäinen merkki.',
        'fi-part': 'sisältää virheen henkilötunnuksen ensimmäisessä merkissä',
        sv: 'Kontrollera första tecknet i personbeteckningen.',
        'sv-part': 'innehåller ett fel i personbeteckningens första tecken'
    },
    // From: ssn-estonia-lithuania, ssn-finland, ssn-latvia
    // Personal identity code year is in the future
    'invalid-year': {
        en: 'Check the first part of the personal identity code.',
        'en-part': 'contains an error in the first part of the personal identity code',
        fi: 'Tarkista henkilötunnuksen alkuosa.',
        'fi-part': 'sisältää virheen henkilötunnuksen alkuosassa',
        sv: 'Kontrollera personbeteckningens början.',
        'sv-part': 'innehåller ett fel i början av personbeteckningen'
    },
    // From: bic, email, iban, op-identifier, pohjola-agreement-id, pohjola-claim-id
    // String does not match regular expression
    'match-fail': {
        en: 'Check the information. The information you gave is in an incorrect format.',
        'en-part': 'is in an incorrect format',
        fi: 'Tarkista tieto. Antamasi tieto on virheellisessä muodossa.',
        'fi-part': 'on virheellisessä muodossa',
        sv: 'Kontrollera uppgiften. Den uppgift du gett är i fel form.',
        'sv-part': 'är i fel form'
    },
    // From: digits
    // has other characters than numbers
    'non-number-chars': {
        en: 'Check the information. The information may only contain numbers.',
        'en-part': 'cannot contain characters other than numbers',
        fi: 'Tarkista tieto. Tieto voi sisältää vain numeroita.',
        'fi-part': 'ei voi sisältää muita merkkejä kuin numeroita',
        sv: 'Kontrollera uppgiften. Uppgiften får endast innehålla siffror.',
        'sv-part': 'kan inte innehålla andra tecken än siffror'
    },
    // From: email, iban, string
    'too-long': {
        en: 'Check the length of the information you entered. The information you gave is too long.',
        'en-part': 'is too long',
        fi: 'Tarkista tiedon pituus. Antamasi tieto on liian pitkä.',
        'fi-part': 'on liian pitkä',
        sv: 'Kontrollera uppgiftens längd. Den uppgift du gett är för lång.',
        'sv-part': 'är för lång'
    },
    // From: iban, string
    'too-short': {
        en: 'Check the length of the information you entered. The information you gave is too short.',
        'en-part': 'is too short',
        fi: 'Tarkista tiedon pituus. Antamasi tieto on liian lyhyt.',
        'fi-part': 'on liian lyhyt',
        sv: 'Kontrollera uppgiftens längd. Den uppgift du gett är för kort.',
        'sv-part': 'är för kort'
    },
    // From: op-iban
    // Bank number of IBAN is wrong
    'unknown-branch': {
        en: 'Check the account number.',
        'en-part': 'is incorrect',
        fi: 'Tarkista tilinumero.',
        'fi-part': 'on virheellinen',
        sv: 'Kontrollera kontonumret.',
        'sv-part': 'är inkorrekt'
    },
    // From: bic, iban
    // Country code of the IBAN doesn't exist
    'unknown-country-code': {
        en: 'Check the country code.',
        'en-part': 'contains an unrecognised country code',
        fi: 'Tarkista maakoodi.',
        'fi-part': 'sisältää tunnistamattoman maakoodin',
        sv: 'Kontrollera landskoden.',
        'sv-part': 'innehåller en oidentifierad landskod'
    },
    // From: plussa-card
    'unknown-grantor': {
        en: 'Check the card number.',
        'en-part': 'is incorrect',
        fi: 'Tarkista kortin numero.',
        'fi-part': 'on virheellinen',
        sv: 'Kontrollera kortets nummer.',
        'sv-part': 'är inkorrekt'
    },
    // From: pohjola-claim-id
    // First two characters do not indicate a known claim type
    'unknown-type': {
        en: 'Check the first two characters.',
        'en-part': 'contains invalid first two characters',
        fi: 'Tarkista kaksi ensimmäistä merkkiä.',
        'fi-part': 'sisältää virheelliset kaksi ensimmäistä merkkiä',
        sv: 'Kontrollera de två första tecknen.',
        'sv-part': 'innehåller inkorrekta två första tecken'
    },
    // From: iban
    // Country code is not in the IBAN registry. Country code can be otherwise valid
    'unsupported-country-code': {
        en: 'The country code of your account number is not supported.',
        'en-part': 'contains a country code that is not supported',
        fi: 'Antamasi tilinumeron maakoodia ei tueta.',
        'fi-part': 'sisältää maakoodin, jota ei tueta',
        sv: 'Landskoden för det kontonummer du uppgett stöds inte.',
        'sv-part': 'innehåller en landskod som inte stöds'
    },
    // From: string
    // Input is not a string
    'unsupported-type': {
        en: 'Check the information. The information you gave is in an incorrect format.',
        'en-part': 'is in an incorrect format',
        fi: 'Tarkista tieto. Antamasi tieto on virheellisessä muodossa.',
        'fi-part': 'on virheellisessä muodossa',
        sv: 'Kontrollera uppgiften. Den uppgift du gett är i fel form.',
        'sv-part': 'är i fel form'
    }
};
